import React, { useState } from "react";
import { Input } from "../ui/input";
import { Controller, useFormContext } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { CalendarDays, EyeClosed, EyeIcon } from "lucide-react";
import { Textarea } from "../ui/textarea";

// import { BooleanSchema } from "yup";
type Props = {
  name: string;
  label?: string;
  type?: string;
  required?: boolean;
  disabled?: Boolean;
  placeholder?: string;
  defaultvalue?: string;
  isNumber?: boolean;
  maxLength?: number | undefined;
  isAlphabet?: boolean;
  isAlphanumeric?: boolean;
  rightComponent?:any;
  isFutureDateDisabled?:boolean;
  maxValue?: number | undefined;
  /** Strip spaces from the start and end of the value. */
  trim?: boolean;
  [key: string]: any; // Allow additional props?;any;
};

const RhfInput = ({
  name,
  label,
  type = "text",
  required = false,
  disabled = false,
  defaultvalue = "",
  isNumber = false,
  maxLength,
  isAlphabet = false,
  isAlphanumeric = false,
  rightComponent=null,
  isFutureDateDisabled=false,
  maxValue,
  trim = false,
  ...props
}: Props) => {
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [dateInputRef, setDateInputRef] = useState<HTMLInputElement | null>(null);
  const { control } = useFormContext();
  
  // Helper function to format date for display (MM/DD/YYYY)
  const formatDateForDisplay = (isoDate: string): string => {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-");
    return `${month}/${day}/${year}`;
  };

  // Helper function to convert display format to ISO (YYYY-MM-DD)
  const formatDateToISO = (displayDate: string): string => {
    if (!displayDate) return "";
    const parts = displayDate.replace(/\//g, "-").split("-");
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return displayDate;
  };

  return (
    <FormField
      control={control}
      defaultValue={defaultvalue}
      name={name}
      render={({ field }) => (
        <FormItem className=" relative">
          {label && (
            <FormLabel>
              {label}
              {required ? "*" : ""}
            </FormLabel>
          )}
          <FormControl>
            <div className="relative">
              {type == "textarea" ? (
                <Textarea {...field} disabled={!!disabled} {...props} />
              ) : (
                <>
                {type === "date" ? (
                  <>
                    <Input
                      {...field}
                      ref={(node) => {
                        setDateInputRef(node);
                        field.ref(node);
                      }}
                      value={field?.value ? field?.value?.split("T")[0] : ""}
                      onChange={(e) => {
                        let val = e.target.value;
                        field.onChange(val);
                      }}
                      type="date"
                      disabled={!!disabled}
                      {...props}
                      className={`date-input-native pr-10 ${props?.className || ""}`}
                      max={
                        isFutureDateDisabled
                          ? new Date().toISOString().split("T")[0]
                          : undefined
                      }
                      lang="en-US"
                      date-date-format="MM/dd/yyyy"
                      style={{ colorScheme: "light" }}
                    />
                    <button
                      type="button"
                      aria-label="Open calendar"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        if (dateInputRef?.showPicker) {
                          dateInputRef.showPicker();
                        } else {
                          dateInputRef?.focus();
                        }
                      }}
                      disabled={!!disabled}
                    >
                      <CalendarDays size={16} />
                    </button>
                  </>
                ) : (
                  <Input
                    // size={"lg"}
                    {...field}
                    onBlur={(e) => {
                      let next = String(e.target.value ?? "");
                      if (trim) next = next.trim();
                      if (type === "email") next = next.toLowerCase();
                      if (next !== e.target.value) field.onChange(next);
                      field.onBlur();
                    }}
                    onChange={(e) => {
                      let val = trim ? e.target.value.trim() : e.target.value;
                      if (type === "email") val = val.toLowerCase();
                      let lastChar = val[val.length - 1] || "";
                      let secondLastChar = val[val.length - 2] || "";

                      // ---------- Prevent spaces in email inputs ----------
                      if (type === "email" && lastChar === " ") {
                        return;
                      }

                      // ---------- Always prevent double space ----------
                      if (type == "password"? lastChar === " " : lastChar === " " && secondLastChar === " ") return;

                      // --------------------------
                      // CASE 1: Only Numbers
                      // --------------------------
                      if (isNumber) {
                        if (val === "" || /^[0-9]+$/.test(val)) {
                          if(maxValue && Number(val) > Number(maxValue)) return;
                          field.onChange(
                            maxLength ? val.substring(0, maxLength) : val,
                          );
                        }
                        return;
                      }

                      // --------------------------
                      // CASE 2: Only Alphabets
                      // --------------------------
                      if (isAlphabet) {
                        const alphabetRegex = /^[A-Za-z ]*$/;

                        // Reject invalid character
                        if (!alphabetRegex.test(val)) return;

                        field.onChange(
                          maxLength ? val.substring(0, maxLength) : val,
                        );
                        return;
                      }

                      // --------------------------
                      // CASE 3: Only Alphanumeric
                      // --------------------------
                      if (isAlphanumeric) {
                        const alphanumericRegex = /^[A-Za-z0-9]*$/;

                        // Reject invalid character
                        if (!alphanumericRegex.test(val)) return;

                        field.onChange(
                          maxLength ? val.substring(0, maxLength) : val,
                        );
                        return;
                      }

                      // --------------------------
                      // Default Input (no restriction)
                      // --------------------------
                      field.onChange(
                        maxLength ? val.substring(0, maxLength) : val,
                      );
                    }}
                    //   defaultValue={defaultvalue}
                    type={isShowPassword ? "text" : type}
                    disabled={!!disabled}
                    {...props}
                  />
                )}
                  {rightComponent ?rightComponent : null}

                </>

              )}

              {type == "password" && (
                <div
                  onClick={() => setIsShowPassword((p) => !p)}
                  className="absolute bottom-2 right-2 cursor-pointer"
                >
                  {!isShowPassword ? (
                    <EyeClosed size={"20"} />
                  ) : (
                    <EyeIcon size={"20"} />
                  )}
                </div>
              )}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default RhfInput;